import Quill from "quill";

// Import Parchment for creating custom attributors
const Parchment = Quill.import("parchment");

// Register custom font sizes with Quill
const SizeStyle = Quill.import("attributors/style/size");
SizeStyle.whitelist = ["10px", "12px", "14px", "16px", "18px", "22px", "24px", "32px"];
Quill.register(SizeStyle, true);

// Function to update font size label based on current selection
export const updateFontSizeLabel = (quill) => {
  if (!quill) return;

  // Find the toolbar associated with this Quill instance
  const getToolbar = () => {
    const container = quill.container;
    if (!container) return null;
    // Find the closest parent with class 'ql-toolbar' or search for it
    const toolbar = container.closest('.ql-container')?.previousElementSibling;
    if (toolbar && toolbar.classList.contains('ql-toolbar')) {
      return toolbar;
    }
    // Fallback: search for toolbar in the same parent
    const parent = container.parentElement;
    if (parent) {
      return parent.querySelector('.ql-toolbar');
    }
    return null;
  };

  const updateLabel = () => {
    const toolbar = getToolbar();
    if (!toolbar) return;

    const sizePicker = toolbar.querySelector('.ql-picker.ql-size .ql-picker-label');
    if (!sizePicker) return;

    // Clear any default text content that Quill might have added
    const textNodes = Array.from(sizePicker.childNodes).filter(node => node.nodeType === Node.TEXT_NODE);
    textNodes.forEach(node => node.remove());

    // Hide any span elements that Quill uses for the default label
    const spans = sizePicker.querySelectorAll('span:not(.ql-picker-label)');
    spans.forEach(span => {
      if (span.textContent && !span.querySelector('svg')) {
        span.style.display = 'none';
      }
    });

    // Use getSelection(false) to avoid stealing focus when user clicks other inputs
    const range = quill.getSelection(false);
    if (!range) {
      // If no selection, use default
      sizePicker.setAttribute('data-label', 'Font Size: 14px');
      return;
    }

    const formats = quill.getFormat(range);
    const fontSize = formats.size || '14px';
    sizePicker.setAttribute('data-label', `Font Size: ${fontSize}`);
  };

  // Update on selection change
  quill.on('selection-change', updateLabel);
  quill.on('text-change', updateLabel);

  // Initial update with a small delay to ensure DOM is ready
  setTimeout(updateLabel, 100);
};


// Define toolbar configurations with custom pixel-based font sizes
export const compactToolbarConfig = {
  toolbar: [
    ["bold", "italic", "underline"],
    [{ color: [] }],
    [{ size: ["10px", "12px", "14px", "16px", "18px", "22px", "24px", "32px"] }],
    [{ align: [] }],
  ],
};

export const fullToolbarConfig = {
  toolbar: [
    ["bold", "italic", "underline"],
    [{ color: [] }],
    [{ size: ["10px", "12px", "14px", "16px", "18px", "22px", "24px", "32px"] }],
    [{ align: [] }],
    ["link", "image"],
    [{ list: "ordered" }, { list: "bullet" }],
  ],
};

export const descriptionToolbarConfig = {
  toolbar: [
    ["bold", "italic", "underline"],
    [{ color: [] }],
    [{ size: ["10px", "12px", "14px", "16px", "18px", "22px", "24px", "32px"] }],
    [{ align: [] }],
    ["link"],
    [{ list: "ordered" }, { list: "bullet" }],
  ],
};

// Custom CSS to style the ReactQuill editor and set default font size
export const quillStyles = `
  .ql-editor {
    font-size: 14px; /* Default font size */
    line-height: 1.15; /* Default line height */
    letter-spacing: 0px; /* Default letter spacing */
    min-height: 40px;
    font-family: 'Roboto', sans-serif; /* Match Material-UI default font */
  }
  .ql-toolbar.ql-snow {
    border: 1px solid #ccc;
    border-bottom: none;
    border-top-left-radius: 4px;
    border-top-right-radius: 4px;
    display: flex;
    align-items: center;
    font-family: 'Roboto', sans-serif; /* Match Material-UI default font */
  }
  .ql-container.ql-snow {
    border: 1px solid #ccc;
    border-top: none;
    border-bottom-left-radius: 4px;
    border-bottom-right-radius: 4px;
  }
  /* Bold, Italic, Underline Buttons */
  .ql-snow .ql-toolbar button {
    position: relative;
  }
  .ql-snow .ql-toolbar button.ql-bold::after {
    content: "Bold";
    position: absolute;
    top: -20px;
    left: 50%;
    transform: translateX(-50%);
    background: #333;
    color: #fff;
    padding: 2px 4px;
    font-size: 10px;
    border-radius: 3px;
    opacity: 0;
    transition: opacity 0.2s;
    pointer-events: none;
    font-family: 'Roboto', sans-serif;
  }
  .ql-snow .ql-toolbar button.ql-bold:hover::after {
    opacity: 1;
  }
  .ql-snow .ql-toolbar button.ql-italic::after {
    content: "Italic";
    position: absolute;
    top: -20px;
    left: 50%;
    transform: translateX(-50%);
    background: #333;
    color: #fff;
    padding: 2px 4px;
    font-size: 10px;
    border-radius: 3px;
    opacity: 0;
    transition: opacity 0.2s;
    pointer-events: none;
    font-family: 'Roboto', sans-serif;
  }
  .ql-snow .ql-toolbar button.ql-italic:hover::after {
    opacity: 1;
  }
  .ql-snow .ql-toolbar button.ql-underline::after {
    content: "Underline";
    position: absolute;
    top: -20px;
    left: 50%;
    transform: translateX(-50%);
    background: #333;
    color: #fff;
    padding: 2px 4px;
    font-size: 10px;
    border-radius: 3px;
    opacity: 0;
    transition: opacity 0.2s;
    pointer-events: none;
    font-family: 'Roboto', sans-serif;
  }
  .ql-snow .ql-toolbar button.ql-underline:hover::after {
    opacity: 1;
  }
  /* Color Picker */
  .ql-snow .ql-picker.ql-color-picker {
    position: relative;
  }
  .ql-snow .ql-picker.ql-color-picker::after {
    content: "Text Color";
    position: absolute;
    top: -20px;
    left: 50%;
    transform: translateX(-50%);
    background: #333;
    color: #fff;
    padding: 2px 4px;
    font-size: 10px;
    border-radius: 3px;
    opacity: 0;
    transition: opacity 0.2s;
    pointer-events: none;
    font-family: 'Roboto', sans-serif;
  }
  .ql-snow .ql-picker.ql-color-picker:hover::after {
    opacity: 1;
  }
  /* Font Size Dropdown */
  .ql-snow .ql-picker.ql-size {
    position: relative;
  }
  .ql-snow .ql-picker.ql-size::after {
    content: "Font Size";
    position: absolute;
    top: -20px;
    left: 50%;
    transform: translateX(-50%);
    background: #333;
    color: #fff;
    padding: 2px 4px;
    font-size: 10px;
    border-radius: 3px;
    opacity: 0;
    transition: opacity 0.2s;
    pointer-events: none;
    font-family: 'Roboto', sans-serif;
  }
  .ql-snow .ql-picker.ql-size:hover::after {
    opacity: 1;
  }
  .ql-snow .ql-picker.ql-size .ql-picker-label {
    font-size: 0 !important;
    line-height: 1;
    overflow: visible;
  }
  .ql-snow .ql-picker.ql-size .ql-picker-label > span {
    display: none !important;
    font-size: 0 !important;
    visibility: hidden !important;
    opacity: 0 !important;
  }
  .ql-snow .ql-picker.ql-size .ql-picker-label::before {
    content: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' height='24' viewBox='0 -960 960 960' width='24'%3E%3Cpath d='M200-160v-640h80v280h240v-280h80v640h-80v-280H280v280h-80Zm600 0v-480h-80v-160h240v160h-80v480h-80Z'/%3E%3C/svg%3E");
    position: absolute;
    left: 4px;
    top: 50%;
    transform: translateY(-50%);
    width: 16px;
    height: 16px;
    z-index: 1;
  }
  .ql-snow .ql-picker.ql-size .ql-picker-label::after {
    content: attr(data-label);
    font-size: 12px !important;
    color: #444 !important;
    display: inline-block !important;
    margin-left: 20px;
    font-family: 'Roboto', sans-serif;
    position: relative;
    line-height: normal;
    z-index: 1;
  }
  .ql-snow .ql-picker.ql-size .ql-picker-label svg {
    display: none;
  }
  .ql-snow .ql-picker.ql-size .ql-picker-item[data-value="10px"]::before {
    content: "Font Size: 10px";
    font-family: 'Roboto', sans-serif;
  }
  .ql-snow .ql-picker.ql-size .ql-picker-item[data-value="12px"]::before {
    content: "Font Size: 12px";
    font-family: 'Roboto', sans-serif;
  }
  .ql-snow .ql-picker.ql-size .ql-picker-item[data-value="14px"]::before {
    content: "Font Size: 14px";
    font-family: 'Roboto', sans-serif;
  }
  .ql-snow .ql-picker.ql-size .ql-picker-item[data-value="16px"]::before {
    content: "Font Size: 16px";
    font-family: 'Roboto', sans-serif;
  }
  .ql-snow .ql-picker.ql-size .ql-picker-item[data-value="18px"]::before {
    content: "Font Size: 18px";
    font-family: 'Roboto', sans-serif;
  }
  .ql-snow .ql-picker.ql-size .ql-picker-item[data-value="22px"]::before {
    content: "Font Size: 22px";
    font-family: 'Roboto', sans-serif;
  }
  .ql-snow .ql-picker.ql-size .ql-picker-item[data-value="24px"]::before {
    content: "Font Size: 24px";
    font-family: 'Roboto', sans-serif;
  }
  .ql-snow .ql-picker.ql-size .ql-picker-item[data-value="32px"]::before {
    content: "Font Size: 32px";
    font-family: 'Roboto', sans-serif;
  }
  /* Additional tooltips for full toolbar */
  .ql-snow .ql-picker.ql-align::after {
    content: "Align";
    position: absolute;
    top: -20px;
    left: 50%;
    transform: translateX(-50%);
    background: #333;
    color: #fff;
    padding: 2px 4px;
    font-size: 10px;
    border-radius: 3px;
    opacity: 0;
    transition: opacity 0.2s;
    pointer-events: none;
    font-family: 'Roboto', sans-serif;
  }
  .ql-snow .ql-picker.ql-align:hover::after {
    opacity: 1;
  }
  .ql-snow .ql-toolbar button.ql-link::after {
    content: "Insert Link";
    position: absolute;
    top: -20px;
    left: 50%;
    transform: translateX(-50%);
    background: #333;
    color: #fff;
    padding: 2px 4px;
    font-size: 10px;
    border-radius: 3px;
    opacity: 0;
    transition: opacity 0.2s;
    pointer-events: none;
    font-family: 'Roboto', sans-serif;
  }
  .ql-snow .ql-toolbar button.ql-link:hover::after {
    opacity: 1;
  }
  .ql-snow .ql-toolbar button.ql-image::after {
    content: "Insert Image";
    position: absolute;
    top: -20px;
    left: 50%;
    transform: translateX(-50%);
    background: #333;
    color: #fff;
    padding: 2px 4px;
    font-size: 10px;
    border-radius: 3px;
    opacity: 0;
    transition: opacity 0.2s;
    pointer-events: none;
    font-family: 'Roboto', sans-serif;
  }
  .ql-snow .ql-toolbar button.ql-image:hover::after {
    opacity: 1;
  }
  .ql-snow .ql-toolbar button.ql-list[value="ordered"]::after {
    content: "Ordered List";
    position: absolute;
    top: -20px;
    left: 50%;
    transform: translateX(-50%);
    background: #333;
    color: #fff;
    padding: 2px 4px;
    font-size: 10px;
    border-radius: 3px;
    opacity: 0;
    transition: opacity 0.2s;
    pointer-events: none;
    font-family: 'Roboto', sans-serif;
  }
  .ql-snow .ql-toolbar button.ql-list[value="ordered"]:hover::after {
    opacity: 1;
  }
  .ql-snow .ql-toolbar button.ql-list[value="bullet"]::after {
    content: "Bullet List";
    position: absolute;
    top: -20px;
    left: 50%;
    transform: translateX(-50%);
    background: #333;
    color: #fff;
    padding: 2px 4px;
    font-size: 10px;
    border-radius: 3px;
    opacity: 0;
    transition: opacity 0.2s;
    pointer-events: none;
    font-family: 'Roboto', sans-serif;
  }
  .ql-snow .ql-toolbar button.ql-list[value="bullet"]:hover::after {
    opacity: 1;
  }
  /* Add spacing between toolbar groups */
  .ql-snow .ql-toolbar .ql-formats {
    margin-right: 8px;
  }
`;
